import React, { useState } from "react";
import { Button, Spinner } from "reactstrap";
import { motion } from "framer-motion";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../GraderUI";
import { formatCategoryLabel } from "../categoryLabels";

const DEFAULT_CATEGORY_INFO = {
	computation: "Weighted signal from the audit model based on site analysis.",
	importance: "Contributes to your overall score for this dimension.",
};

const CATEGORY_INFO = {
	faq_readiness: {
		computation:
			"Based on 4 signals: FAQPage schema presence, dedicated FAQ pages, question-format headings, and FAQ coverage on product pages.",
		importance:
			"AI systems rely on FAQ content to answer user questions directly. Strong FAQ coverage increases your chances of being cited in AI responses.",
	},
	schema: {
		computation:
			"Analyzes structured data types (Product, Organization, FAQPage, etc.) and their completeness across pages.",
		importance:
			"Structured data helps AI understand your content context, products, and relationships, improving accurate representation in responses.",
	},
	answerability: {
		computation:
			"Based on 5 answer-formatting signals: question-format headings, list/step usage, concise sentences, H2 sectioning, and FAQPage/HowTo/QAPage schema.",
		importance:
			"Content structured as clear, scannable answers is far more likely to be extracted and cited verbatim by AI answer engines.",
	},
	technical: {
		computation:
			"Checks 5 signals: robots.txt presence, XML sitemap, HTTPS enabled, canonical tag coverage, and meta description coverage.",
		importance:
			"Technical foundations ensure AI crawlers can access, index, and understand your site structure properly.",
	},
	readability: {
		computation:
			"Analyzes content structure, heading hierarchy, paragraph length, sentence complexity, and scannable formatting.",
		importance:
			"Clear, readable content helps AI models accurately interpret and summarize your information for users.",
	},
	content_depth: {
		computation:
			"Measures average word count per page, topic completeness, and content comprehensiveness across the site.",
		importance:
			"Comprehensive content provides AI with sufficient context to confidently cite your site as an authoritative source.",
	},
	internal_linking: {
		computation:
			"Calculates average internal links per content page. Target: 5+ links per page for optimal cross-page context.",
		importance:
			"Strong internal linking helps AI understand topic relationships and content hierarchy across your site.",
	},
	crawlability: {
		computation:
			"Evaluates bot accessibility, indexability signals, and crawl efficiency across pages.",
		importance:
			"Proper crawlability ensures AI systems can discover and access all your important content.",
	},
	metadata: {
		computation:
			"Checks meta description quality, title tag optimization, Open Graph tags, and metadata completeness.",
		importance:
			"Quality metadata helps AI understand page context and improves how your content appears in AI-generated summaries.",
	},
	multimodal: {
		computation:
			"Analyzes image alt text quality, video transcripts, and multimedia content optimization.",
		importance:
			"Multimodal signals help AI understand non-text content, expanding citation opportunities beyond written text.",
	},
	e_clarity: {
		computation:
			"Evaluates product page completeness, attribute clarity, and structured product information.",
		importance:
			"Clear product details help AI accurately recommend and describe your products in shopping queries.",
	},
	brand_entity: {
		computation:
			"Measures brand name mentions, entity recognition signals, and brand identity consistency across content.",
		importance:
			"Strong brand entity signals ensure AI correctly identifies and attributes your brand in generated responses.",
	},
	ai_citation: {
		computation:
			"Tracks organic citation rate (no web search) - tests if AI models know your brand from training data.",
		importance:
			"Measures brand authority in AI training data. High scores mean AI models cite you without needing to search.",
	},
	ai_discoverability: {
		computation:
			"Evaluates visibility in AI powered search and discovery mechanisms.",
		importance:
			"High discoverability means your content appears more frequently in AI-generated recommendations and answers.",
	},
	trust_signals: {
		computation:
			"Analyzes authority markers, credentials, customer reviews, security badges, and third-party validation.",
		importance:
			"Trust signals increase AI confidence in citing your content, especially for sensitive or high-stakes topics.",
	},
	freshness: {
		computation: "Assesses content recency and update frequency",
		importance:
			"Fresh, up to date content is prioritized by AI systems. Date signals help AI determine content relevance and timeliness.",
	},
	sentiment: {
		computation: "Analyzes tone, sentiment, and emotional context as perceived by AI language models.",
		importance:
			"Positive, professional sentiment improves how AI frames and presents your brand in generated responses.",
	},
	brand_recognition: {
		computation:
			"Measures brand visibility and recognition consistency across multiple AI models and platforms.",
		importance:
			"Consistent brand recognition ensures AI systems correctly identify and represent your brand across contexts.",
	},
	eeat: {
		computation:
			"A consolidated 0-100 score blending four pillars: Trust 35% (trust signals + reviews, certifications & policies), Authoritativeness 30% (AI Citation + Brand Recognition), Expertise 20% (named authors, bios, credentials, cited sources), Experience 15% (first-hand language, reviews, case studies, original media).",
		importance:
			"E-E-A-T is how AI assistants judge whether your brand is credible enough to cite and recommend. It absorbs Brand Recognition, AI Citation and Trust signals into one view and adds Experience & Expertise — the strongest predictor of being chosen in AI answers.",
	},
	llms_txt: {
		computation:
			"Checks for an llms.txt file at your site root (and that it is well-formed: an H1 title plus markdown links to key pages).",
		importance:
			"llms.txt is an emerging standard that gives AI answer engines a curated, easy-to-parse map of your most important content — helping them find and cite the right pages.",
	},
};

function formatWeightPercent(weight) {
	const n = Number(weight);
	if (!Number.isFinite(n)) return "0";
	return String(Math.round(n));
}

function getBarColor(score) {
	if (score >= 80) return "#10b981";
	if (score >= 60) return "#f59e0b";
	return "#ef4444";
}

function getScoreFillClass(score) {
	if (score >= 80) return "score-fill--excellent";
	if (score >= 60) return "score-fill--good";
	return "score-fill--poor";
}

function getScoreLabel(score) {
	if (score >= 80) return "Excellent";
	if (score >= 60) return "Good";
	if (score >= 40) return "Needs Work";
	return "Critical";
}

function getScoreAnalysis(scorePercentage) {
	if (scorePercentage >= 80) return "✓ Performing well - maintain current standards";
	if (scorePercentage >= 60) return "→ Room for improvement - focus on gaps identified below";
	if (scorePercentage >= 40) return "⚠ Significant gaps - prioritize fixes in this category";
	return "✗ Critical issues - immediate attention required";
}

function CategoryTooltip({ category, score, deterministicScores, cardType }) {
	const info = CATEGORY_INFO[category] || DEFAULT_CATEGORY_INFO;
	const label = formatCategoryLabel(category);
	const scoreColor = getBarColor(score);
	const scoreLabel = getScoreLabel(score);
	const scorePercentage = Math.round(score);
	const det = deterministicScores?.[category];
	const evidence = Array.isArray(det?.evidence) ? det.evidence : [];
	const gaps = Array.isArray(det?.gaps) ? det.gaps : [];
	const showScoreAnalysis = cardType !== "geo" || category === "freshness";

	return (
		<div className="category-tooltip">
			<div className="category-tooltip__header">
				<div className="category-tooltip__title">{label}</div>
				<div className="category-tooltip__score-row">
					<div className="category-tooltip__track-wrap">
						<div className="category-tooltip__track">
							<div
								className="category-tooltip__fill"
								style={{ width: `${scorePercentage}%`, background: scoreColor }}
							/>
						</div>
						<span
							className="category-tooltip__thumb"
							style={{
								left: `${scorePercentage}%`,
								background: scoreColor,
								borderColor: scoreColor,
							}}
						/>
					</div>
					<div className="category-tooltip__score-value" style={{ color: scoreColor }}>
						{scorePercentage}/100
					</div>
				</div>
				<div className="category-tooltip__score-label" style={{ color: scoreColor }}>
					{scoreLabel}
				</div>
			</div>

			{showScoreAnalysis && (
				<div className="category-tooltip__section">
					<div className="category-tooltip__section-title">CURRENT SCORE ANALYSIS</div>
					{evidence.length > 0 && (
						<ul className="category-tooltip__evidence-list">
							{evidence.map((item) => (
								<li key={item} className="category-tooltip__evidence-item">
									<span className="category-tooltip__icon category-tooltip__icon--pass">✓</span>
									{item}
								</li>
							))}
						</ul>
					)}
					{gaps.length > 0 && (
						<ul className="category-tooltip__gap-list">
							{gaps.map((item) => (
								<li key={item} className="category-tooltip__gap-item">
									<span className="category-tooltip__icon category-tooltip__icon--fail">✕</span>
									{item}
								</li>
							))}
						</ul>
					)}
					{evidence.length === 0 && gaps.length === 0 && (
						<div className="category-tooltip__analysis">{getScoreAnalysis(scorePercentage)}</div>
					)}
				</div>
			)}

			<div className="category-tooltip__section">
				<div className="category-tooltip__section-title">HOW IT&apos;S COMPUTED</div>
				<div className="category-tooltip__text">{info.computation}</div>
			</div>

			<div className="category-tooltip__section">
				<div className="category-tooltip__section-title">WHY IT MATTERS</div>
				<div className="category-tooltip__text">{info.importance}</div>
			</div>
		</div>
	);
}

function ScoreBreakdownBar({ category, score, weight, deterministicScores, cardType }) {
	const [showTooltip, setShowTooltip] = useState(false);
	const barColor = getBarColor(score);
	const fillClass = getScoreFillClass(score);

	return (
		<div className="score-breakdown-bar">
			<span className="score-breakdown-bar__label">
				{formatCategoryLabel(category)}
				<span
					className="score-breakdown-bar__info-wrap"
					onMouseEnter={() => setShowTooltip(true)}
					onMouseLeave={() => setShowTooltip(false)}
				>
					<button
						type="button"
						className={`score-breakdown-bar__info${showTooltip ? " score-breakdown-bar__info--active" : ""}`}
						aria-label={`Info about ${formatCategoryLabel(category)}`}
					>
						i
					</button>
					{showTooltip && (
						<CategoryTooltip
							category={category}
							score={score}
							deterministicScores={deterministicScores}
							cardType={cardType}
						/>
					)}
				</span>
			</span>
			<div className="score-breakdown-bar__track">
				<div
					className={`score-breakdown-bar__fill ${fillClass}`}
					style={{ width: `${Math.round(score)}%` }}
				/>
			</div>
			<span className="score-breakdown-bar__score" style={{ color: barColor }}>
				{Math.round(score)}
			</span>
			<span className="score-breakdown-bar__weight">{formatWeightPercent(weight)}%</span>
		</div>
	);
}

export function ScoreCard({
	type,
	grade,
	score,
	breakdown = {},
	weights = {},
	deterministicScores,
	className = "",
	onGenerateLlmsTxt,
	llmsTxtSubmitting = false,
}) {
	const [breakdownExpanded, setBreakdownExpanded] = useState(true);
	const [expanded, setExpanded] = useState(false);
	const label =
		type === "aeo" ? "Answer Engine Optimisation" : "Generative Engine Optimisation";

	const sortedCategories = Object.entries(weights).sort(([, a], [, b]) => b - a);
	const biggestMover = sortedCategories
		.map(([cat, weight]) => {
			const w = Math.round(Number(weight) || 0);
			const catScore = breakdown[cat] ?? 0;
			return {
				category: cat,
				weight: w,
				score: catScore,
				impact: w * (100 - catScore),
			};
		})
		.sort((a, b) => b.impact - a.impact)[0];

	const progressPct = Math.round(score);
	const fillClass = getScoreFillClass(score);

	return (
		<div className={`score-card score-card--${type} score-card--in-tab ${className}`.trim()}>
			<div className="score-card__header">
				<p className="score-card__type-label">{type.toUpperCase()} Score</p>
				<div className="score-card__grade-row">
					<span className="score-card__grade">{grade}</span>
					<span className="score-card__score">{score.toFixed(1)}</span>
					<span className="score-card__max">/100</span>
				</div>
				<div className="score-card__progress">
					<div
						className={`score-card__progress-fill ${fillClass}`}
						style={{ width: `${progressPct}%` }}
					/>
				</div>
				<p className="score-card__subtitle">{label}</p>
			</div>

			<div className="score-card__breakdown">
				<Collapsible open={breakdownExpanded} onOpenChange={setBreakdownExpanded}>
					<CollapsibleTrigger className="score-card__breakdown-trigger">
						<span>Score breakdown</span>
						<span>{breakdownExpanded ? "↑" : "↓"}</span>
					</CollapsibleTrigger>
					<CollapsibleContent>
						<div className="score-card__breakdown-body">
							{sortedCategories.map(([category, weight]) => {
								const isBiggestMover =
									biggestMover && category === biggestMover.category;
								const categoryScore = breakdown[category] ?? 0;
								return (
									<div key={category} className="score-card__breakdown-row">
										<div className="score-card__breakdown-bar-wrap">
											<ScoreBreakdownBar
												category={category}
												score={categoryScore}
												weight={weight}
												deterministicScores={deterministicScores}
												cardType={type}
											/>
											{category === "llms_txt" && categoryScore < 80 && (
												<div className="score-card__llms-txt-cta">
													<p className="score-card__llms-txt-message">
														No llms.txt file found - generate one to help AI answer engines discover your key pages.
													</p>
													<Button
														color="primary"
														size="sm"
														className="score-card__llms-txt-btn"
														disabled={llmsTxtSubmitting || !onGenerateLlmsTxt}
														onClick={onGenerateLlmsTxt}
													>
														{llmsTxtSubmitting ? (
															<>
																<Spinner size="sm" className="me-2" />
																Starting…
															</>
														) : (
															"Generate llms.txt"
														)}
													</Button>
												</div>
											)}
										</div>
										{/* {isBiggestMover && (
											<span className="score-card__impact-badge">Biggest impact</span>
										)} */}
									</div>
								);
							})}
						</div>
					</CollapsibleContent>
				</Collapsible>
			</div>

			<div className="score-card__methodology">
				<Collapsible open={expanded} onOpenChange={setExpanded}>
					<CollapsibleTrigger className="score-card__methodology-trigger">
						<span>How is this score calculated?</span>
						<span>{expanded ? "↑" : "↓"}</span>
					</CollapsibleTrigger>
					<CollapsibleContent>
						{/* <div className="score-card__methodology-body">
							<p>
								This score is a weighted average of {sortedCategories.length} signals. Each
								category contributes proportionally to its weight shown above.
							</p>
							<p>
								To improve your grade fastest, focus on high-weight categories scoring below
								70. The top 3 highest-weight signals account for{" "}
								{sortedCategories
									.slice(0, 3)
									.reduce((s, [, w]) => s + Math.round(Number(w) || 0), 0)}
								% of your total score.
							</p>
						</div> */}
		


		<div className="score-card__methodology-body">
  {biggestMover ? (
    <p>
      Improving <strong>{formatCategoryLabel(biggestMover.category)}</strong> by 10
      points could add ~{Math.round(biggestMover.weight / 10)} points to your{" "}
      {type.toUpperCase()} score, or ~{Math.round(biggestMover.weight / 1000)} of your
      total score.
    </p>
  ) : null}
</div>
					</CollapsibleContent>
				</Collapsible>
			</div>
		</div>
	);
}

export function OverallScoreSummary({ overallScore, overallGrade, aeoScore, geoScore }) {
	return (
		<div className="overall-score-summary">
			<div className="overall-score-summary__container">
				<motion.div
					className="overall-score-summary__card"
					initial={{ opacity: 0, y: 12 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.4 }}
				>
					<div className="overall-score-summary__main">
						<p className="overall-score-summary__label">AI Visibility Score</p>
						<div className="overall-score-summary__grade-row">
							<span className="overall-score-summary__grade">{overallGrade || "N/A"}</span>
							<span className="overall-score-summary__score">
								{overallScore != null ? overallScore.toFixed(1) : "—"}
							</span>
							<span className="overall-score-summary__max">/100</span>
						</div>
					</div>

					<div className="overall-score-summary__combined">
						<p className="overall-score-summary__combined-title">Combined Score</p>
						<div className="overall-score-summary__combined-scores">
							<span className="overall-score-summary__combined-value overall-score-summary__combined-value--aeo">
								AEO: {aeoScore != null ? aeoScore.toFixed(1) : "—"}
							</span>
							<span className="overall-score-summary__combined-value overall-score-summary__combined-value--geo">
								GEO: {geoScore != null ? geoScore.toFixed(1) : "—"}
							</span>
						</div>
					</div>
				</motion.div>
			</div>
		</div>
	);
}
